/* eslint-disable react/jsx-curly-brace-presence */
import React, { useCallback, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Flex from '@atlaskit/primitives/components/flex';
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { usePageSpaceKey } from '@confluence/page-context';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController.ts';
import { fg } from '@confluence/feature-gating';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { MAX_GROUP_SIZE } from '../presenceUtils.ts';
import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes.ts';

import { PresenceAvatarButton } from './PresenceAvatarButton.tsx';

const NoCollaboratorsDialog = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-NoCollaboratorsDialog" */ './NoCollaboratorsDialog.tsx'
			)
		).NoCollaboratorsDialog,
});

const PresenceMenuWithGuest = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-PresenceMenuWithGuest" */ './PresenceMenuWithGuest.tsx'
			)
		).PresenceMenuWithGuest,
});

const i18n = defineMessages({
	overflowLabel: {
		id: 'team-presence.avatar-group.overflow-label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence avatar group overflow menu',
	},
	blogpostLandmark: {
		id: 'team-presence.avatar-group.blogpost-landmark',
		defaultMessage: 'Active people on blog post',
		description: 'Accessible region landmark for presence avatar group when on a blog post',
	},
	pageLandmark: {
		id: 'team-presence.avatar-group.page-landmark',
		defaultMessage: 'Active people on page',
		description: 'Accessible region landmark for presence avatar group when on a page',
	},
});

export type PresenceAvatarGroupProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	participants: Participant[];
	analyticSource?: string;
	popupOpen?: boolean;
	totalParticipants: number;
};
const popupContainerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	position: 'relative',
	width: '360px',
});

const editObjectHeaderStyles = xcss({
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
});

const overflowMoreStyles = xcss({
	padding: 'space.075',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'radius.full',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.subtle',
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	font: 'font.heading.xxsmall',
	':hover': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreActiveStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

// Base styles for indicator - margin will be conditionally applied based on feature gate
const indicatorBaseStyles = xcss({
	minWidth: '28px',
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
	borderRadius: 'radius.small',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineOffset: 'space.050',
	},
});

// Legacy margin styles (used when platform spacing fix is disabled)
const indicatorLegacyMarginStyles = xcss({
	margin: 'space.025',
});

const indicatorOverflowStyles = xcss({
	minWidth: '35px',
	maxWidth: '35px',
});

export const PresenceAvatarGroup = ({
	contentId,
	contentMode,
	contentType,
	analyticSource,
	popupOpen,
	participants,
	totalParticipants,
}: PresenceAvatarGroupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLoggedIn } = useSessionData();
	const isGuest = useIsGuest();
	const isExternal = isExternalShareRequest();
	const [spaceKey] = usePageSpaceKey();

	const [isPopupOpen, setIsPopupOpen] = useState(popupOpen || false);
	const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

	const isEligibleForEmptyPresenceDialog = useCallback(() => {
		// only 1 user, not anonymous and not guest user
		return participants.length === 1 && isLoggedIn && !isGuest && !isExternal;
	}, [participants, isLoggedIn, isGuest, isExternal]);

	useEffect(() => {
		if (isPopupOpen && participants.length > 1 && participants.length <= MAX_GROUP_SIZE) {
			setIsPopupOpen(false);
		}
	}, [isPopupOpen, participants]);

	const handleShareClick = useCallback(
		(analyticsTriggerSource: string) => {
			setIsPopupOpen(false);
			const shareDialogType = 'v2';
			setIsShareDialogOpen(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticsTriggerSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceShare',
					attributes: {
						contentId,
						contentMode,
						contentType,
						shareDialog: shareDialogType,
					},
				},
			}).fire();
		},
		[contentId, contentMode, contentType, createAnalyticsEvent],
	);

	const toggleEmptyPresenceDialog = useCallback(() => {
		setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'singlePresenceAvatarGroup',
				product: 'confluence',
				attributes: {
					contentId,
					contentMode,
					contentType,
					toggle: isPopupOpen ? 'close' : 'open',
				},
			},
		}).fire();
	}, [contentId, contentMode, contentType, analyticSource, createAnalyticsEvent, isPopupOpen]);

	const isSingleParticipant = Boolean(participants && totalParticipants === 1);

	const onMoreClick = useCallback(
		(e: React.MouseEvent<Element, MouseEvent>, number: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceAvatarGroupMoreButton',
					product: 'confluence',
					attributes: {
						contentId,
						contentType,
						contentMode,
						number,
					},
				},
			}).fire();

			setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

			// platform-design-system-popup-ref causes the popup to close when the user clicks on the more button,
			// revisit what other types of changes we can put in place to not have to call stopPropagation
			e.stopPropagation();
		},
		[contentId, contentType, contentMode, analyticSource, createAnalyticsEvent],
	);

	const getTriggerId = () => {
		return isSingleParticipant ? 'no-collaborators-dialog' : 'presence-menu';
	};

	const triggerId = getTriggerId();

	const renderNoCollaboratorsDialog = () => (
		<NoCollaboratorsDialog
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			spaceKey={spaceKey || ''}
			onShareClick={handleShareClick}
			testId={triggerId}
		/>
	);

	const renderPresenceMenu = () => (
		<PresenceMenuWithGuest
			contentId={contentId}
			contentType={contentType}
			contentMode={contentMode}
			participants={participants}
			totalParticipants={totalParticipants}
		/>
	);

	const getPopUpContent = () => {
		return isSingleParticipant ? renderNoCollaboratorsDialog() : renderPresenceMenu();
	};

	const PopupContent = getPopUpContent();

	const overflowParticipantCount = totalParticipants - MAX_GROUP_SIZE + 1;

	const landmarkRegionLabel =
		contentType === 'blogpost'
			? formatMessage(i18n.blogpostLandmark)
			: formatMessage(i18n.pageLandmark);

	const isEditObjectHeader = contentMode === 'edit' || contentMode === 'live';

	return (
		<>
			<ExperienceSuccess name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
			{participants.length === 1 && isShareDialogOpen && (
				<ShareAndRestrictDialogController
					contentId={contentId}
					contentType={contentType}
					allowRestrictions
					pageModes="view"
					refetchButtonData={() => {}}
					onClose={() => {
						setIsShareDialogOpen(false);
					}}
					isUnpublishedDraft={false}
				/>
			)}
			<Popup
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				placement="bottom-end"
				content={() => <Box xcss={popupContainerStyles}>{PopupContent}</Box>}
				trigger={(triggerProps) => (
					<Box
						{...triggerProps}
						paddingInline="space.050"
						aria-label={landmarkRegionLabel}
						role="region"
						xcss={isEditObjectHeader && editObjectHeaderStyles}
						data-vc="presence-avatar-group"
					>
						{fg('platform-dst-motion-uplift') ? (
							<AvatarGroup
								appearance="stack"
								size="small"
								maxCount={MAX_GROUP_SIZE}
								data={participants}
								testId="presence-avatar-group"
								label={landmarkRegionLabel}
								borderColor={token('color.background.neutral')}
								onMoreClick={(e) => onMoreClick(e, overflowParticipantCount)}
								shouldPopupRenderToParent={false}
								isTooltipDisabled
								overrides={{
									Avatar: {
										render: (_, props, index) => (
											<Flex alignItems="center">
												<PresenceAvatarButton
													index={index}
													participant={participants[index]}
													contentMode={contentMode}
													contentType={contentType}
													contentId={contentId}
													isSingleParticipant={isSingleParticipant}
													isEligibleForEmptyPresenceDialog={isEligibleForEmptyPresenceDialog()}
													toggleEmptyPresenceDialog={toggleEmptyPresenceDialog}
													avatarProps={props}
													analyticSource={analyticSource}
												/>
											</Flex>
										),
									},
									MoreIndicator: {
										render: (_, { buttonProps, isActive: __, borderColor: ___, ...rest }) => (
											<Pressable
												type="submit"
												{...buttonProps}
												{...rest}
												padding={fg('platform-avatar-group-spacing-fix') ? 'space.0' : 'space.025'}
												onClick={rest.onClick}
												xcss={[
													indicatorBaseStyles,
													!fg('platform-avatar-group-spacing-fix') && indicatorLegacyMarginStyles,
													// TODO: This is a bug, anything above 1000 will break...
													overflowParticipantCount > 100 && indicatorOverflowStyles,
												]}
												testId={rest.testId}
												data-vc="presence-overflow"
												aria-label={formatMessage(i18n.overflowLabel)}
											>
												<Box xcss={[overflowMoreStyles, isPopupOpen && moreActiveStyles]}>
													{overflowParticipantCount}
												</Box>
											</Pressable>
										),
									},
								}}
							/>
						) : (
							<AvatarGroup
								appearance="stack"
								size="small"
								maxCount={MAX_GROUP_SIZE}
								data={participants}
								testId="presence-avatar-group"
								label={landmarkRegionLabel}
								borderColor={token('color.background.neutral')}
								onMoreClick={(e) => onMoreClick(e, overflowParticipantCount)}
								shouldPopupRenderToParent={false}
								isTooltipDisabled
								overrides={{
									Avatar: {
										render: (_, props, index) => {
											return (
												<Flex alignItems="center" key={`PresenceAvatarButton-${index}`}>
													<PresenceAvatarButton
														index={index}
														participant={participants[index]}
														contentMode={contentMode}
														contentType={contentType}
														contentId={contentId}
														isSingleParticipant={isSingleParticipant}
														isEligibleForEmptyPresenceDialog={isEligibleForEmptyPresenceDialog()}
														toggleEmptyPresenceDialog={toggleEmptyPresenceDialog}
														avatarProps={props}
														analyticSource={analyticSource}
													/>
												</Flex>
											);
										},
									},
									MoreIndicator: {
										render: (_, { buttonProps, isActive: __, borderColor: ___, ...rest }) => (
											<Pressable
												type="submit"
												{...buttonProps}
												{...rest}
												padding={fg('platform-avatar-group-spacing-fix') ? 'space.0' : 'space.025'}
												onClick={rest.onClick}
												xcss={[
													indicatorBaseStyles,
													!fg('platform-avatar-group-spacing-fix') && indicatorLegacyMarginStyles,
													// TODO: This is a bug, anything above 1000 will break...
													overflowParticipantCount > 100 && indicatorOverflowStyles,
												]}
												testId={rest.testId}
												data-vc="presence-overflow"
												aria-label={formatMessage(i18n.overflowLabel)}
											>
												<Box xcss={[overflowMoreStyles, isPopupOpen && moreActiveStyles]}>
													{overflowParticipantCount}
												</Box>
											</Pressable>
										),
									},
								}}
							/>
						)}
					</Box>
				)}
			/>
		</>
	);
};
